import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  acknowledgeReferenceVersionRubric,
  getReferenceVersionRubric,
  type RubricAcknowledgmentState,
  type RubricPayload,
  type RubricTaskDef
} from '../../lib/classworkApi';

interface Props {
  classworkId: number;
  referenceSetVersionId: number | null | undefined;
  /** Optionaler Refresh-Hook, wenn die Elternansicht den Status mitpflegt. */
  onAcknowledged?: (state: RubricAcknowledgmentState) => void;
}

function formatPoints(n: number): string {
  if (!Number.isFinite(n)) return '–';
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace('.', ',');
}

function rubricSourceLabel(source?: string | null): { label: string; tone: 'amber' | 'emerald' | 'slate' } {
  switch (source) {
    case 'llm_autogen_no_rubric':
      return { label: 'KI-vorgeschlagen (kein Raster hochgeladen)', tone: 'amber' };
    case 'llm_extraction':
      return { label: 'Aus hochgeladenem Raster extrahiert', tone: 'emerald' };
    case 'rubric_json':
      return { label: 'Strukturiert hinterlegt', tone: 'emerald' };
    default:
      return { label: 'Unbekannt', tone: 'slate' };
  }
}

export default function ClassworkRubricAcknowledgePanel({
  classworkId,
  referenceSetVersionId,
  onAcknowledged
}: Props): JSX.Element | null {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const enabled = Boolean(classworkId && referenceSetVersionId);
  const rubricQuery = useQuery(
    ['classwork-reference-rubric', classworkId, referenceSetVersionId],
    () => getReferenceVersionRubric(classworkId, Number(referenceSetVersionId)),
    { enabled, staleTime: 15_000 }
  );

  const rubric: RubricPayload | null = rubricQuery.data?.rubric ?? null;
  const ack: RubricAcknowledgmentState | null = rubricQuery.data?.acknowledgment ?? null;
  const source = rubric?.source || ack?.source || null;
  const tasks: RubricTaskDef[] = Array.isArray(rubric?.tasks) ? (rubric!.tasks as RubricTaskDef[]) : [];

  const totals = useMemo(() => {
    if (!rubric) return null;
    return {
      base: rubric.totalPointsBase ?? 0,
      withBonus: rubric.totalPointsWithBonus ?? rubric.totalPointsBase ?? 0,
      bonus: Math.max(0, (rubric.totalPointsWithBonus ?? 0) - (rubric.totalPointsBase ?? 0))
    };
  }, [rubric]);

  if (!enabled) return null;
  if (rubricQuery.isLoading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        Korrekturraster wird geladen …
      </div>
    );
  }
  if (!rubric || !tasks.length) {
    // Kein Raster verfügbar — entweder noch nicht versucht, oder die Ableitung hat nichts geliefert.
    const rubricStatus = ack?.status || 'not_attempted';
    if (rubricStatus === 'failed') {
      return (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          Das Ableiten eines Korrekturrasters ist fehlgeschlagen. Ohne Raster ist die Korrektur nur eine
          Orientierung – bitte Musterlösung/Erwartungshorizont prüfen und Referenzversion neu bauen.
        </div>
      );
    }
    return null;
  }

  const srcMeta = rubricSourceLabel(source);
  const isAutogen = source === 'llm_autogen_no_rubric';
  const ackedMeta = ack?.acknowledgedMeta || null;
  const alreadyAcked = Boolean(ack?.acknowledged);
  const mustAck = Boolean(ack?.blocksCorrection);

  const panelTone =
    isAutogen && mustAck
      ? 'border-amber-400 bg-amber-50/70'
      : srcMeta.tone === 'amber'
      ? 'border-amber-300 bg-amber-50/50'
      : 'border-emerald-200 bg-emerald-50/40';

  const handleAcknowledge = async (): Promise<void> => {
    if (!referenceSetVersionId || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const next = await acknowledgeReferenceVersionRubric(
        classworkId,
        Number(referenceSetVersionId),
        notes.trim() || undefined
      );
      setNotes('');
      await queryClient.invalidateQueries(['classwork-reference-rubric', classworkId, referenceSetVersionId]);
      await queryClient.invalidateQueries(['classwork-reference-versions', classworkId]);
      if (onAcknowledged && next) onAcknowledged(next);
    } catch (err: any) {
      setSubmitError(String(err?.response?.data?.error || err?.message || 'Speichern fehlgeschlagen'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`rounded-lg border ${panelTone}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 px-3 pt-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Korrekturraster zu dieser Klassenarbeit</h4>
          <p className="text-[11px] text-slate-600 mt-0.5">
            Quelle: <span className="font-medium">{srcMeta.label}</span>
            {rubric.model ? <> · Modell: <code className="text-[11px]">{rubric.model}</code></> : null}
            {rubric.extractedAt ? (
              <> · erstellt: {new Date(rubric.extractedAt).toLocaleString('de-DE')}</>
            ) : null}
          </p>
        </div>
        <div className="text-right text-[11px] text-slate-700">
          <div>
            Pflicht: <strong>{formatPoints(totals?.base ?? 0)}</strong> P
            {totals && totals.bonus > 0 ? <> · Bonus: <strong>{formatPoints(totals.bonus)}</strong> P</> : null}
          </div>
          <div>
            {tasks.length} Aufgabe{tasks.length === 1 ? '' : 'n'}
          </div>
        </div>
      </div>

      {isAutogen ? (
        <div className="mx-3 mt-2 rounded-md border border-amber-300 bg-amber-100/60 px-3 py-2 text-[12px] text-amber-900">
          <strong>Wichtig:</strong> Für diese Klassenarbeit liegt kein Korrekturraster hochgeladen vor. Lernrakete
          hat daraufhin aus Aufgabenstellung und ggf. Musterlösung einen Vorschlag abgeleitet. Die
          Punkteverteilung ist eine <em>KI-Schätzung</em>. Bitte prüfen und {alreadyAcked ? 'zur Kenntnis genommen' : 'zur Kenntnis nehmen'}
          , bevor Korrekturen damit laufen.
        </div>
      ) : null}

      <div className="px-3 pb-3 pt-2 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-slate-600">
              <th className="pr-3 pb-1">Aufgabe</th>
              <th className="pr-3 pb-1">Max. Punkte</th>
              <th className="pr-3 pb-1">Typ</th>
              <th className="pb-1">Kriterien / Hinweise</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={`${t.id}-${t.label}`} className="border-t border-slate-200 align-top">
                <td className="pr-3 py-1.5 font-medium text-slate-900">
                  <span className="text-[11px] text-slate-500 mr-1">{t.id}</span>
                  {t.label}
                </td>
                <td className="pr-3 py-1.5 tabular-nums">{formatPoints(Number(t.pointsMax))}</td>
                <td className="pr-3 py-1.5">
                  {t.bonus ? (
                    <span className="inline-flex items-center rounded-full bg-amber-200 text-amber-900 px-2 py-0.5 text-[10px] font-semibold uppercase">
                      Bonus
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-slate-200 text-slate-700 px-2 py-0.5 text-[10px] font-semibold uppercase">
                      Pflicht
                    </span>
                  )}
                </td>
                <td className="py-1.5 text-slate-700">{t.criteria || '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rubric.notes ? (
          <p className="mt-2 text-[11px] text-slate-600 italic">Hinweis der Ableitung: {rubric.notes}</p>
        ) : null}
      </div>

      {isAutogen ? (
        <div className="border-t border-amber-200 bg-white/60 px-3 py-2 space-y-2">
          {alreadyAcked ? (
            <div className="text-[12px] text-emerald-800 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-900 px-2 py-0.5 text-[10px] font-semibold uppercase">
                Zur Kenntnis genommen
              </span>
              {ack?.acknowledgedAt ? (
                <span>am {new Date(ack.acknowledgedAt).toLocaleString('de-DE')}</span>
              ) : null}
              {ackedMeta?.userName ? <span>von {ackedMeta.userName}</span> : null}
              {ackedMeta?.notes ? <span>· „{ackedMeta.notes}“</span> : null}
            </div>
          ) : (
            <>
              <label className="block text-[11px] font-medium text-slate-700">
                Optionaler Kommentar (z. B. „Punkteverteilung passt aus meiner Sicht“)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                placeholder="optional"
              />
              {submitError ? (
                <div className="text-[11px] text-rose-700">{submitError}</div>
              ) : null}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={submitting}
                  onClick={() => void handleAcknowledge()}
                >
                  {submitting ? 'Speichere …' : 'Zur Kenntnis nehmen & freigeben'}
                </button>
                <span className="text-[11px] text-slate-600">
                  Korrekturen starten erst nach dieser Freigabe.
                </span>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
